import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatTooltipModule } from '@angular/material/tooltip'
import { SatPopover, SatPopoverModule } from '@ncstate/sat-popover'
import { TranslateModule } from '@ngx-translate/core'

import { benefitValuesMatch } from '../../services/benefits-sync'

export type BalanceBenefitCellFormat = 'currency' | 'percent' | 'currency-nullable'

@Component({
  selector: 'mmx-balance-benefit-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    SatPopoverModule,
    TranslateModule,
  ],
  templateUrl: './balance-benefit-cell.component.html',
  styleUrls: ['./balance-benefit-cell.component.scss'],
})
export class BalanceBenefitCellComponent implements OnInit {
  @Input({ required: true }) label!: string
  @Input({ required: true }) control!: FormControl<number | null>
  @Input() format: BalanceBenefitCellFormat = 'currency'
  @Input() originalValue: number | null = null
  @Input() ambiguous = false

  @Output() readonly committed = new EventEmitter<void>()

  private readonly cdr = inject(ChangeDetectorRef)
  private readonly destroyRef = inject(DestroyRef)

  // The popover holds a draft so users can cancel by pressing Escape.
  draft: number | null = null

  ngOnInit(): void {
    // displayValue/modified/isNone read control.value via getters. Under OnPush, a programmatic
    // reset (e.g. Discard's patchValue) mutates the control in place without changing the @Input
    // reference or firing a local event, so the view stayed stale until an unrelated DOM event
    // (a hover) triggered CD. Mark for check on every value change to keep the display in sync.
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck())
  }

  get displayValue(): number | null {
    const value = this.control.value
    if (value == null) {
      return null
    }

    return this.format === 'percent' ? this.fractionToPercent(value) : value
  }

  get isNone(): boolean {
    return this.control.value == null
  }

  get modified(): boolean {
    if (this.ambiguous || this.originalValue == null) {
      return false
    }

    return !benefitValuesMatch(this.control.value, this.originalValue)
  }

  get supportsNone(): boolean {
    return this.format === 'currency-nullable'
  }

  // Mirrors the bound control's disabled state so a read-only benefits form
  // (panel calls form.disable()) makes the cell view-only: no popover, no
  // edit/reset affordances.
  get disabled(): boolean {
    return this.control.disabled
  }

  onOpen(popover: SatPopover): void {
    if (this.disabled) {
      return
    }
    this.draft = this.displayValue
    popover.open()
  }

  onDraftChange(value: number | null): void {
    this.draft = value
  }

  onNoneChange(event: MatCheckboxChange): void {
    this.draft = event.checked ? null : 0
  }

  onClosed(): void {
    const next = this.normalizeDraft(this.draft)
    if (next !== this.control.value) {
      this.control.setValue(next)
      this.committed.emit()
    }
  }

  onPopoverKeyup(event: KeyboardEvent, popover: SatPopover): void {
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.stopPropagation()
      if (event.key === 'Escape') {
        this.draft = this.displayValue
      }
      popover.close()
      ;(event.target as HTMLElement).blur()
    }
  }

  reset(): void {
    this.control.setValue(this.originalValue)
    this.committed.emit()
  }

  private normalizeDraft(raw: number | null): number | null {
    if (raw == null) {
      return this.supportsNone ? null : 0
    }
    const clamped = this.format === 'percent' ? Math.min(Math.max(raw, 0), 100) : Math.max(raw, 0)
    return this.format === 'percent' ? this.percentToFraction(clamped) : clamped
  }

  private fractionToPercent(value: number): number {
    return Math.round(value * 10000) / 100
  }

  private percentToFraction(value: number): number {
    return Math.round(value * 100) / 10000
  }
}
